// ============================================================================
// Stored values in words. States, priorities and labels are enum values in the
// data (`next`, `paused`, `energy:high`); a chip or a select says them as a
// person would ("Next", "Paused", "High energy"). Pure, tested.
// ============================================================================

export const TASK_STATE_WORD: Record<string, string> = {
  inbox: 'Inbox',
  next: 'Next',
  waiting: 'Waiting',
  someday: 'Someday',
  done: 'Done',
  dropped: 'Dropped',
}

export const PROJECT_STATE_WORD: Record<string, string> = {
  active: 'Active',
  paused: 'Paused',
  done: 'Finished',
  dropped: 'Dropped',
}

/** Priority 0–3 as the task form's select reads it; the row chips keep their marks. */
export const PRIORITY_WORD = ['Normal', 'High', 'Higher', 'Urgent'] as const

const ENERGY_WORD: Record<string, string> = {
  low: 'Low energy',
  med: 'Medium energy',
  medium: 'Medium energy',
  high: 'High energy',
}

const upperFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * How a label reads on a chip. The common ones say only their value ("home");
 * energy says what it measures ("High energy", so it never reads as urgency
 * beside a priority); `from` says who ("From Keira"); anything else keeps its
 * key, set apart from the value ("Client: acme"), so `key:value` never shows
 * as typed.
 */
export const labelWords = (key: string, value: string): string => {
  if (key === 'area' || key === 'context' || key === 'tag') return value
  if (key === 'energy') return ENERGY_WORD[value.toLowerCase()] ?? `${upperFirst(value)} energy`
  if (key === 'from') return `From ${upperFirst(value)}`
  return `${upperFirst(key)}: ${value}`
}

/**
 * What someone types into a label field, as a label: `key:value` (or
 * `key=value`) as it is, "high energy" and the like as energy, and a plain
 * word as an area (which every list shows as the word alone).
 */
export const parseLabelEntry = (text: string): [string, string] | undefined => {
  const t = text.trim()
  if (!t) return undefined
  const kv = /^([a-z0-9_-]+)\s*[:=]\s*(.+)$/i.exec(t)
  if (kv) return [kv[1]!.toLowerCase(), kv[2]!.trim()]
  const energy = /^(low|med|medium|high)\s+energy$/i.exec(t)
  if (energy) return ['energy', energy[1]!.toLowerCase() === 'med' ? 'medium' : energy[1]!.toLowerCase()]
  return ['area', t.toLowerCase().replace(/\s+/g, '-')]
}
